import { useState, useCallback, useMemo, useEffect } from "react";
import { createPortal } from "react-dom";
import type { ColumnVisibility } from "../datagrid-configs/types";
import "./ColumnVisibilityModal.css";

export interface CustomView {
	id: string;
	name: string;
	visibility: ColumnVisibility;
	groupOrder?: string[];
	groupColors?: Record<string, string>;
	isDefault?: boolean;
}

export type GroupColors = Record<string, string>;

/** Derive group order from column order so the modal reflects the current table order */
function deriveGroupOrderFromColumnOrder(
	columnOrder: string[],
	columnGroups: Record<string, Array<{ key: string; label: string }>>
): string[] {
	const groupList = Object.keys(columnGroups);
	const keyToGroup = new Map<string, string>();
	groupList.forEach((group) => {
		(columnGroups[group] || []).forEach((col) => keyToGroup.set(col.key, group));
	});
	const seen = new Set<string>();
	const result: string[] = [];
	columnOrder.forEach((key) => {
		const group = keyToGroup.get(key);
		if (group && !seen.has(group)) {
			seen.add(group);
			result.push(group);
		}
	});
	groupList.forEach((g) => {
		if (!seen.has(g)) result.push(g);
	});
	return result;
}

interface ColumnVisibilityModalProps {
	/** Columns grouped by category */
	columnGroups: Record<string, Array<{ key: string; label: string }>>;
	/** Current visibility state */
	columnVisibility: ColumnVisibility;
	/** Default visibility state (for reset) */
	defaultVisibility?: ColumnVisibility;
	/** Callback when visibility changes */
	onVisibilityChange: (visibility: ColumnVisibility, order?: string[], colors?: GroupColors) => void;
	/** Callback to close the modal */
	onClose: () => void;
	/** Current column order (optional) */
	columnOrder?: string[];
	/** Default column order (for reset) */
	defaultColumnOrder?: string[];
	/** Saved custom views */
	customViews?: CustomView[];
	/** Currently selected custom view ID */
	selectedCustomViewId?: string | null;
	/** Callback when custom views are updated (save/delete) */
	onCustomViewsChange?: (views: CustomView[], selectedId: string | null) => void;
	/** Current group colors */
	groupColors?: GroupColors;
}

/**
 * ColumnVisibilityModal - Modal for configuring which column groups are visible.
 * Shows groups with toggles - toggling a group shows/hides all columns in that group.
 * Supports saving and loading custom views.
 */
export function ColumnVisibilityModal({
	columnGroups,
	columnVisibility,
	defaultVisibility,
	onVisibilityChange,
	onClose,
	columnOrder: initialOrder,
	defaultColumnOrder,
	customViews = [],
	selectedCustomViewId = null,
	onCustomViewsChange,
	groupColors: initialGroupColors = {},
}: ColumnVisibilityModalProps) {
	// Get all columns flattened
	const allColumns = useMemo(() => {
		const cols: Array<{ key: string; label: string; group: string }> = [];
		Object.entries(columnGroups).forEach(([group, groupCols]) => {
			groupCols.forEach((col) => {
				cols.push({ ...col, group });
			});
		});
		return cols;
	}, [columnGroups]);

	// Get group names in order
	const groups = useMemo(() => Object.keys(columnGroups), [columnGroups]);

	// State
	const [localVisibility, setLocalVisibility] = useState<ColumnVisibility>(columnVisibility);
	const [columnOrder, setColumnOrder] = useState<string[]>(initialOrder || allColumns.map((c) => c.key));
	const [isCreatingCustomView, setIsCreatingCustomView] = useState(false);
	const [customViewName, setCustomViewName] = useState("");
	const [draggedGroup, setDraggedGroup] = useState<string | null>(null);
	const [localCustomViews, setLocalCustomViews] = useState<CustomView[]>(customViews);
	const [localSelectedViewId, setLocalSelectedViewId] = useState<string | null>(selectedCustomViewId);
	const [localGroupColors, setLocalGroupColors] = useState<GroupColors>(() => {
		if (selectedCustomViewId) {
			const selectedView = customViews.find((v) => v.id === selectedCustomViewId);
			if (selectedView?.groupColors) {
				return selectedView.groupColors;
			}
		}
		return initialGroupColors;
	});

	// Initialize group order from selected custom view, or derive from current column order so modal matches table
	const [groupOrder, setGroupOrder] = useState<string[]>(() => {
		if (selectedCustomViewId) {
			const selectedView = customViews.find((v) => v.id === selectedCustomViewId);
			if (selectedView?.groupOrder) {
				return selectedView.groupOrder;
			}
		}
		const order = initialOrder || allColumns.map((c) => c.key);
		return deriveGroupOrderFromColumnOrder(order, columnGroups);
	});

	// Check if a group is visible (all columns in group are visible)
	const isGroupVisible = useCallback(
		(group: string): boolean => {
			const groupColumns = columnGroups[group] || [];
			if (groupColumns.length === 0) return false;
			return groupColumns.every((col) => localVisibility[col.key] !== false);
		},
		[columnGroups, localVisibility]
	);

	// Check if a group is partially visible (some columns visible, some hidden)
	const isGroupPartiallyVisible = useCallback(
		(group: string): boolean => {
			const groupColumns = columnGroups[group] || [];
			if (groupColumns.length === 0) return false;
			const visibleCount = groupColumns.filter((col) => localVisibility[col.key] !== false).length;
			return visibleCount > 0 && visibleCount < groupColumns.length;
		},
		[columnGroups, localVisibility]
	);

	// Check if any columns are visible (for confirm button state)
	const hasVisibleColumns = useMemo(() => {
		return Object.values(localVisibility).some((v) => v !== false);
	}, [localVisibility]);

	// Helper to update custom view in the list
	const updateCustomViewInList = useCallback(
		(viewId: string, newVisibility: ColumnVisibility, newGroupOrder: string[], newGroupColors?: GroupColors) => {
			setLocalCustomViews((prev) =>
				prev.map((view) =>
					view.id === viewId
						? { ...view, visibility: newVisibility, groupOrder: newGroupOrder, groupColors: newGroupColors || view.groupColors }
						: view
				)
			);
		},
		[]
	);

	// Count currently visible columns (for "at least one" safeguard)
	const visibleColumnCount = useMemo(() => {
		return Object.values(localVisibility).filter((v) => v !== false).length;
	}, [localVisibility]);

	const minVisibleColumns = 1;

	// Toggle all columns in a group (prevent hiding if it would leave zero columns visible)
	const handleGroupToggle = useCallback(
		(group: string) => {
			const groupColumns = columnGroups[group] || [];
			const allVisible = isGroupVisible(group);

			if (allVisible) {
				const wouldLeaveVisible = visibleColumnCount - groupColumns.length;
				if (wouldLeaveVisible < minVisibleColumns) {
					return; // Do not allow hiding the last group(s)
				}
			}

			const newVisibility = { ...localVisibility };
			groupColumns.forEach((col) => {
				newVisibility[col.key] = !allVisible;
			});

			setLocalVisibility(newVisibility);

			// Update the selected custom view if one is selected
			if (localSelectedViewId) {
				updateCustomViewInList(localSelectedViewId, newVisibility, groupOrder, localGroupColors);
			}
		},
		[columnGroups, isGroupVisible, localVisibility, localSelectedViewId, groupOrder, localGroupColors, updateCustomViewInList, visibleColumnCount]
	);

	// Handle group color change
	const handleGroupColorChange = useCallback(
		(group: string, color: string) => {
			const newColors = { ...localGroupColors };
			if (color) {
				newColors[group] = color;
			} else {
				delete newColors[group];
			}
			setLocalGroupColors(newColors);

			// Update the selected custom view if one is selected
			if (localSelectedViewId) {
				updateCustomViewInList(localSelectedViewId, localVisibility, groupOrder, newColors);
			}
		},
		[localGroupColors, localSelectedViewId, localVisibility, groupOrder, updateCustomViewInList]
	);

	// Select a custom view (ensure at least one column stays visible)
	const handleSelectCustomView = useCallback(
		(viewId: string) => {
			const view = localCustomViews.find((v) => v.id === viewId);
			if (view) {
				const newGroupOrder = view.groupOrder || groups;
				let visibility = view.visibility;
				const visibleCount = Object.values(visibility).filter((v) => v !== false).length;
				if (visibleCount < minVisibleColumns && allColumns.length > 0) {
					visibility = { ...visibility, [allColumns[0].key]: true };
				}
				setLocalVisibility(visibility);
				setGroupOrder(newGroupOrder);
				setLocalGroupColors(view.groupColors || {});
				setLocalSelectedViewId(viewId);

				// Recalculate column order based on the new group order (same logic as drag handler)
				setColumnOrder((prev) => {
					const newColumnOrder: string[] = [];

					newGroupOrder.forEach((group) => {
						const groupCols = columnGroups[group] || [];
						groupCols.forEach((col) => {
							if (prev.includes(col.key)) {
								newColumnOrder.push(col.key);
							}
						});
					});

					// Add any columns not in groups
					prev.forEach((key) => {
						if (!newColumnOrder.includes(key)) {
							newColumnOrder.push(key);
						}
					});

					return newColumnOrder;
				});
			}
		},
		[localCustomViews, groups, columnGroups, allColumns]
	);

	// Deselect custom view (go back to default)
	const handleDeselectCustomView = useCallback(() => {
		// Reset to defaults
		const defaults =
			defaultVisibility ||
			(() => {
				const vis: ColumnVisibility = {};
				Object.values(columnGroups)
					.flat()
					.forEach((col) => {
						vis[col.key] = true;
					});
				return vis;
			})();

		setLocalVisibility(defaults);
		setLocalSelectedViewId(null);
		setLocalGroupColors({});

		// Reset group order to original
		setGroupOrder(groups);

		// Sync column order: use defaultColumnOrder when provided, else derive from group order
		if (defaultColumnOrder) {
			setColumnOrder(defaultColumnOrder);
		} else {
			const derivedOrder: string[] = [];
			groups.forEach((group) => {
				const groupCols = columnGroups[group] || [];
				groupCols.forEach((col) => derivedOrder.push(col.key));
			});
			setColumnOrder(derivedOrder);
		}
	}, [defaultVisibility, columnGroups, defaultColumnOrder, groups]);

	// Save a new custom view with default state (all visible, default order, no colors)
	const handleSaveCustomView = useCallback(() => {
		if (!customViewName.trim()) return;

		const defaultVis =
			defaultVisibility ||
			(() => {
				const vis: ColumnVisibility = {};
				Object.values(columnGroups)
					.flat()
					.forEach((col) => {
						vis[col.key] = true;
					});
				return vis;
			})();

		const newView: CustomView = {
			id: `custom_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
			name: customViewName.trim(),
			visibility: { ...defaultVis },
			groupOrder: [...groups],
			groupColors: {},
		};

		setLocalCustomViews((prev) => [...prev, newView]);
		setLocalSelectedViewId(newView.id);
		setCustomViewName("");
		setIsCreatingCustomView(false);

		// Sync modal state to the new view (defaults) so the list reflects what was saved
		setLocalVisibility({ ...defaultVis });
		setGroupOrder([...groups]);
		setLocalGroupColors({});
		if (defaultColumnOrder) {
			setColumnOrder(defaultColumnOrder);
		} else {
			const derivedOrder: string[] = [];
			groups.forEach((group) => {
				const groupCols = columnGroups[group] || [];
				groupCols.forEach((col) => derivedOrder.push(col.key));
			});
			setColumnOrder(derivedOrder);
		}
	}, [customViewName, defaultVisibility, columnGroups, defaultColumnOrder, groups]);

	// Delete a custom view
	const handleDeleteCustomView = useCallback(
		(viewId: string, e: React.MouseEvent) => {
			e.stopPropagation();
			setLocalCustomViews((prev) => prev.filter((v) => v.id !== viewId));
			if (localSelectedViewId === viewId) {
				setLocalSelectedViewId(null);
			}
		},
		[localSelectedViewId]
	);

	// Toggle default view
	const handleToggleDefaultView = useCallback((viewId: string, e: React.MouseEvent) => {
		e.stopPropagation();
		setLocalCustomViews((prev) =>
			prev.map((view) => ({
				...view,
				isDefault: view.id === viewId ? !view.isDefault : false,
			}))
		);
	}, []);

	// Reset to default visibility
	// NOTE: This preserves the "isDefault" setting on custom views - only resets visibility/colors/order
	const handleReset = useCallback(() => {
		// Build default visibility if not provided
		const defaults =
			defaultVisibility ||
			(() => {
				const vis: ColumnVisibility = {};
				Object.values(columnGroups)
					.flat()
					.forEach((col) => {
						vis[col.key] = true;
					});
				return vis;
			})();

		setLocalVisibility(defaults);
		setLocalSelectedViewId(null);
		setLocalGroupColors({});

		// Reset group order to original
		setGroupOrder(groups);

		// Always sync column order with reset state: use defaultColumnOrder when provided,
		// otherwise derive from reset group order so Confirm does not send stale order
		if (defaultColumnOrder) {
			setColumnOrder(defaultColumnOrder);
		} else {
			const derivedOrder: string[] = [];
			groups.forEach((group) => {
				const groupCols = columnGroups[group] || [];
				groupCols.forEach((col) => derivedOrder.push(col.key));
			});
			setColumnOrder(derivedOrder);
		}

		// Note: localCustomViews is intentionally NOT modified here
		// This preserves the "isDefault" flag on custom views
	}, [defaultVisibility, columnGroups, defaultColumnOrder, groups]);

	// Drag and drop handlers for groups (no-op while creating a new view)
	const handleDragStart = useCallback(
		(e: React.DragEvent, group: string) => {
			if (isCreatingCustomView) return;
			setDraggedGroup(group);
			e.dataTransfer.effectAllowed = "move";
			e.dataTransfer.setData("text/plain", group);
		},
		[isCreatingCustomView]
	);

	const handleDragOver = useCallback((e: React.DragEvent) => {
		e.preventDefault();
		e.dataTransfer.dropEffect = "move";
	}, []);

	const handleDrop = useCallback(
		(e: React.DragEvent, targetGroup: string) => {
			e.preventDefault();
			if (!draggedGroup || draggedGroup === targetGroup) return;

			// Calculate new group order
			const newGroupOrder = [...groupOrder];
			const draggedIndex = newGroupOrder.indexOf(draggedGroup);
			let targetIndex = newGroupOrder.indexOf(targetGroup);

			newGroupOrder.splice(draggedIndex, 1);
			if (draggedIndex < targetIndex) {
				targetIndex--;
			}
			newGroupOrder.splice(targetIndex, 0, draggedGroup);

			setGroupOrder(newGroupOrder);

			// Also reorder the columns based on new group order
			setColumnOrder((prev) => {
				const newColumnOrder: string[] = [];

				newGroupOrder.forEach((group) => {
					const groupCols = columnGroups[group] || [];
					groupCols.forEach((col) => {
						if (prev.includes(col.key)) {
							newColumnOrder.push(col.key);
						}
					});
				});

				// Add any columns not in groups
				prev.forEach((key) => {
					if (!newColumnOrder.includes(key)) {
						newColumnOrder.push(key);
					}
				});

				return newColumnOrder;
			});

			// Update the selected custom view if one is selected
			if (localSelectedViewId) {
				updateCustomViewInList(localSelectedViewId, localVisibility, newGroupOrder, localGroupColors);
			}

			setDraggedGroup(null);
		},
		[draggedGroup, groupOrder, columnGroups, localSelectedViewId, localVisibility, localGroupColors, updateCustomViewInList]
	);

	const handleDragEnd = useCallback(() => {
		setDraggedGroup(null);
	}, []);

	// Confirm handler
	const handleConfirm = useCallback(() => {
		// Save custom views changes
		if (onCustomViewsChange) {
			onCustomViewsChange(localCustomViews, localSelectedViewId);
		}
		onVisibilityChange(localVisibility, columnOrder, localGroupColors);
		onClose();
	}, [localCustomViews, localSelectedViewId, onCustomViewsChange, localVisibility, columnOrder, localGroupColors, onVisibilityChange, onClose]);

	// Toggle custom view creation
	const handleCustomViewClick = useCallback(() => {
		setIsCreatingCustomView((prev) => !prev);
		if (isCreatingCustomView) {
			setCustomViewName("");
		}
	}, [isCreatingCustomView]);

	// Prevent body scroll when modal is open
	useEffect(() => {
		document.body.style.overflow = "hidden";
		return () => {
			document.body.style.overflow = "";
		};
	}, []);

	// Handle backdrop click
	const handleBackdropClick = useCallback(
		(e: React.MouseEvent) => {
			if (e.target === e.currentTarget) {
				onClose();
			}
		},
		[onClose]
	);

	// Handle escape key
	useEffect(() => {
		const handleEscape = (e: KeyboardEvent) => {
			if (e.key === "Escape") {
				onClose();
			}
		};
		document.addEventListener("keydown", handleEscape);
		return () => document.removeEventListener("keydown", handleEscape);
	}, [onClose]);

	return createPortal(
		<div className="colVisModal-overlay" onClick={handleBackdropClick}>
			<div className="colVisModal">
				{/* Header */}
				<div className="colVisModal-header">
					<h2 className="colVisModal-title">Columns visibility</h2>
					<button type="button" className="colVisModal-closeBtn" onClick={onClose} aria-label="Close">
						<svg width="14" height="14" viewBox="0 0 14 14" fill="none">
							<path d="M1 1L13 13M1 13L13 1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
						</svg>
					</button>
				</div>

				{/* Content */}
				<div className="colVisModal-content">
					{/* Saved Custom Views */}
					{localCustomViews.length > 0 && (
						<div className="colVisModal-savedViews">
							<span className="colVisModal-filterLabel">Saved Views</span>
							<div className="colVisModal-viewChips">
								{localCustomViews.map((view) => (
									<button
										key={view.id}
										type="button"
										className={`colVisModal-viewChip ${localSelectedViewId === view.id ? "colVisModal-viewChip--active" : ""} ${view.isDefault ? "colVisModal-viewChip--default" : ""}`}
										onClick={() =>
											localSelectedViewId === view.id ? handleDeselectCustomView() : handleSelectCustomView(view.id)
										}
									>
										<span
											className={`colVisModal-viewChip-star ${view.isDefault ? "colVisModal-viewChip-star--active" : ""}`}
											onClick={(e) => handleToggleDefaultView(view.id, e)}
											title={view.isDefault ? "Remove as default view" : "Set as default view"}
										>
											★
										</span>
										{view.name}
										<span
											className="colVisModal-viewChip-delete"
											onClick={(e) => handleDeleteCustomView(view.id, e)}
											title="Delete view"
										>
											×
										</span>
									</button>
								))}
							</div>
						</div>
					)}

					{/* Custom View button */}
					<button
						type="button"
						className={`colVisModal-customViewBtn ${isCreatingCustomView ? "colVisModal-customViewBtn--active" : ""}`}
						onClick={handleCustomViewClick}
					>
						+ Custom View
					</button>

					{/* Custom View Name Input */}
					{isCreatingCustomView && (
						<div className="colVisModal-customViewInput">
							<label className="colVisModal-inputLabel">View Name</label>
							<div className="colVisModal-inputRow">
								<input
									type="text"
									className="colVisModal-input"
									placeholder="Enter view name..."
									value={customViewName}
									onChange={(e) => setCustomViewName(e.target.value)}
									onKeyDown={(e) => {
										if (e.key === "Enter" && customViewName.trim()) {
											handleSaveCustomView();
										}
									}}
								/>
								<button
									type="button"
									className="colVisModal-saveBtn"
									onClick={handleSaveCustomView}
									disabled={!customViewName.trim()}
								>
									Save
								</button>
							</div>
						</div>
					)}

					{/* Group list */}
					<div className="colVisModal-columnList">
						{groupOrder.map((group) => {
							const visible = isGroupVisible(group);
							const partial = isGroupPartiallyVisible(group);

							return (
								<div
									key={group}
									className={`colVisModal-columnRow ${draggedGroup === group ? "colVisModal-columnRow--dragging" : ""} ${isCreatingCustomView ? "colVisModal-columnRow--disabled" : ""}`}
									draggable={!isCreatingCustomView}
									onDragStart={(e) => handleDragStart(e, group)}
									onDragOver={handleDragOver}
									onDrop={(e) => handleDrop(e, group)}
									onDragEnd={handleDragEnd}
								>
									{/* Drag handle */}
									<div className="colVisModal-dragHandle">
										<svg width="10" height="16" viewBox="0 0 10 16" fill="currentColor">
											<circle cx="2" cy="2" r="1.5" />
											<circle cx="8" cy="2" r="1.5" />
											<circle cx="2" cy="8" r="1.5" />
											<circle cx="8" cy="8" r="1.5" />
											<circle cx="2" cy="14" r="1.5" />
											<circle cx="8" cy="14" r="1.5" />
										</svg>
									</div>

									{/* Color picker */}
									<div className="colVisModal-colorPicker">
										<input
											type="color"
											className="colVisModal-colorInput"
											value={localGroupColors[group] || "#ffffff"}
											onChange={(e) => handleGroupColorChange(group, e.target.value === "#ffffff" ? "" : e.target.value)}
											title={`Set background color for ${group}`}
											disabled={isCreatingCustomView}
										/>
										{localGroupColors[group] && (
											<button
												type="button"
												className="colVisModal-colorClear"
												onClick={() => handleGroupColorChange(group, "")}
												title="Clear color"
												disabled={isCreatingCustomView}
											>
												×
											</button>
										)}
									</div>

									{/* Group name */}
									<span className="colVisModal-columnName">{group}</span>

									{/* Toggle switch */}
									<button
										type="button"
										className={`colVisModal-toggle ${visible ? "colVisModal-toggle--on" : ""} ${partial ? "colVisModal-toggle--partial" : ""}`}
										onClick={() => handleGroupToggle(group)}
										aria-label={`Toggle ${group} group`}
										disabled={isCreatingCustomView}
									>
										<span className="colVisModal-toggleKnob" />
									</button>
								</div>
							);
						})}
					</div>
				</div>

				{/* Footer */}
				<div className="colVisModal-footer">
					<button
						type="button"
						className="colVisModal-btn colVisModal-btn--reset"
						onClick={handleReset}
						disabled={isCreatingCustomView}
					>
						Reset
					</button>
					<div className="colVisModal-footerRight">
						<button type="button" className="colVisModal-btn colVisModal-btn--cancel" onClick={onClose}>
							Cancel
						</button>
						<button
							type="button"
							className={`colVisModal-btn colVisModal-btn--confirm ${hasVisibleColumns ? "colVisModal-btn--confirm-active" : ""}`}
							onClick={handleConfirm}
							disabled={!hasVisibleColumns}
							title={!hasVisibleColumns ? "At least one column must remain visible" : undefined}
						>
							Confirm
						</button>
					</div>
				</div>
			</div>
		</div>,
		document.body
	);
}

export default ColumnVisibilityModal;
